'use client';

import { useQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import Layout from '@/components/layout/Layout';
import HeroSlider from '@/components/home/HeroSlider';
import ProductCategories from '@/components/products/ProductCategories';
import ProductGrid from '@/components/products/ProductGrid';
import CaseGallery from '@/components/home/CaseGallery';
import CaseList from '@/components/home/CaseList';
import BannerImage from '@/components/home/BannerImage';
import NewsCenter from '@/components/home/NewsCenter';
import WhyVIKDA from '@/components/home/WhyVIKDA';
import { settingAPI } from '@/lib/api';

// 强制对齐样式
const alignmentStyles = `
  .elementor-element-08e26db .elementor-container.elementor-column-gap-default {
    max-width: 1200px !important;
    margin-left: auto !important;
    margin-right: auto !important;
    padding-left: 20px !important;
    padding-right: 20px !important;
    box-sizing: border-box !important;
    width: 100% !important;
  }
  .elementor-element-16aac7c {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
  .elementor-element-16aac7c .elementor-widget-wrap {
    max-width: 100% !important;
    width: 100% !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
  .elementor-element-285f9bd {
    max-width: 1200px !important;
    margin-left: auto !important;
    margin-right: auto !important;
    padding-left: 20px !important;
    padding-right: 20px !important;
    box-sizing: border-box !important;
    width: 100% !important;
  }
  .elementor-element-285f9bd .elementor-container.elementor-column-gap-default {
    max-width: 100% !important;
    width: 100% !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
  .elementor-element-4860806 .elementor-container.elementor-column-gap-default {
    max-width: 1200px !important;
    margin-left: auto !important;
    margin-right: auto !important;
    padding-left: 20px !important;
    padding-right: 20px !important;
    box-sizing: border-box !important;
    width: 100% !important;
  }
  .elementor-element-01d2b44 {
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
  .elementor-element-01d2b44 .elementor-widget-wrap {
    max-width: 100% !important;
    width: 100% !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    box-sizing: border-box !important;
  }
  .elementor-element-be8562e .elementor-container.elementor-column-gap-default {
    max-width: 1200px !important;
    margin-left: auto !important;
    margin-right: auto !important;
    padding-left: 20px !important;
    padding-right: 20px !important;
    box-sizing: border-box !important;
    width: 100% !important;
  }
  .elementor-element-2436317 {
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  .elementor-element-858a311 {
    max-width: 100% !important;
    width: 100% !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    box-sizing: border-box !important;
  }
  .elementor-element-858a311 .elementor-container.elementor-column-gap-default {
    max-width: 100% !important;
    width: 100% !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    box-sizing: border-box !important;
  }
  #ast-desktop-header .ast-container {
    max-width: 1200px !important;
    margin-left: auto !important;
    margin-right: auto !important;
    padding-left: 20px !important;
    padding-right: 20px !important;
    box-sizing: border-box !important;
    width: 100% !important;
  }
  .ast-main-header-wrap.main-header-bar-wrap,
  #main-header-sticky {
    position: sticky !important;
    top: 0 !important;
    z-index: 9999 !important;
    background-color: #ffffff !important;
    transition: box-shadow 0.3s ease, background-color 0.3s ease !important;
    width: 100% !important;
    margin: 0 !important;
    left: 0 !important;
    right: 0 !important;
    display: flex !important;
    flex-direction: column !important;
  }
  .ast-main-header-wrap.main-header-bar-wrap.ast-sticky-active,
  #main-header-sticky.ast-sticky-active {
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1) !important;
  }
  #ast-desktop-header {
    overflow: visible !important;
  }
  #masthead {
    overflow: visible !important;
  }
`;

export default function HomePage() {
  // 获取首页内容设置
  const { data: settingsData, isLoading: settingsLoading, error: settingsError } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      try {
        return await settingAPI.getAll();
      } catch (error) {
        console.error('[HomePage] Failed to fetch settings:', error);
        // 返回空对象，使用默认值
        return {};
      }
    },
    staleTime: 0, // 设置为0，确保每次都获取最新数据
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 1, // 只重试1次
    retryDelay: 1000, // 重试延迟1秒
    throwOnError: false, // 即使失败也继续渲染
  });

  // settingAPI.getAll() 现在直接返回 Settings 对象，而不是包装在 data 中
  const settings = settingsData || {};
  
  // 调试日志
  console.log('[HomePage] Settings data:', {
    settingsData,
    settings,
    isLoading: settingsLoading,
    error: settingsError,
    productCenterTitle: settings.product_center_title,
    productCenterDescription: settings.product_center_description,
  });
  
  // 使用设置中的值，如果没有则使用默认值
  const productCenterTitle = settings.product_center_title || 'Product Center';
  const productCenterDescription = settings.product_center_description || 'The leading company of excavators, pile driving machinery, road machinery, port machinery, roadheaders and large-tonnage cranes in China';
  const cooperationCaseTitle = settings.cooperation_case_title || 'Cooperation Case';
  const cooperationCaseDescription = settings.cooperation_case_description || 'SANY aims to attract the most talented individuals and offer them rich job experiences and career development opportunities. While working at SANY, you\'ll have the opportunity to work in various locations, experience different cultures, and acquire a global outlook.';

  // 如果设置加载失败，使用默认值，不阻塞渲染
  console.log('[HomePage] Rendering homepage, settingsLoading:', settingsLoading, 'error:', settingsError);

  return (
    <Layout>
        <style dangerouslySetInnerHTML={{ __html: alignmentStyles }} />
        <article
          className="post-9 page type-page status-publish has-post-thumbnail ast-article-single"
          id="post-9"
          itemType="https://schema.org/CreativeWork"
          itemScope
        >
          <header className="entry-header ast-no-title ast-header-without-markup">
          </header>

          <div className="entry-content clear" itemProp="text">
            <div data-elementor-type="wp-page" data-elementor-id="9" className="elementor elementor-9" data-elementor-post-type="page">
              {/* Hero轮播图区域 - 全宽 */}
              <section className="elementor-section elementor-top-section elementor-element elementor-element-5edffa8 elementor-section-full_width elementor-section-height-default elementor-section-height-default" data-id="5edffa8" data-element_type="section">
                <div className="elementor-container elementor-column-gap-no">
                  <div className="elementor-column elementor-col-100 elementor-top-column elementor-element elementor-element-827551a" data-id="827551a" data-element_type="column">
                    <div className="elementor-widget-wrap elementor-element-populated">
                      <div className="elementor-element elementor-element-75888ec elementor--h-position-center elementor--v-position-middle elementor-arrows-position-inside elementor-pagination-position-inside elementor-widget elementor-widget-slides" data-id="75888ec" data-element_type="widget" data-settings='{"navigation":"both","autoplay":"yes","pause_on_hover":"yes","pause_on_interaction":"yes","autoplay_speed":5000,"infinite":"yes","transition":"slide","transition_speed":500}' data-widget_type="slides.default">
                        <div className="elementor-widget-container">
                          <Suspense fallback={<div style={{ height: '400px', background: '#f0f0f0' }}>Loading slider...</div>}>
                            <HeroSlider />
                          </Suspense>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

            {/* 产品中心区域 - 匹配Elementor模板结构 */}
            <section className="product-center-section elementor-section elementor-top-section elementor-element elementor-element-204db98 elementor-section-full_width elementor-section-height-default elementor-section-height-default" data-id="204db98" data-element_type="section" data-settings='{"background_background":"classic"}' style={{ backgroundColor: '#F8F8F8', background: '#F8F8F8', backgroundImage: 'none', width: '100vw', position: 'relative', left: '50%', right: '50%', marginLeft: '-50vw', marginRight: '-50vw', maxWidth: 'none', padding: '50px 0', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              {/* 外层section：标题和描述区域 - 匹配参考网站结构 */}
              <div className="elementor-container elementor-column-gap-default">
                <div className="elementor-column elementor-col-100 elementor-top-column elementor-element elementor-element-ea16887" data-id="ea16887" data-element_type="column">
                  <div className="elementor-widget-wrap elementor-element-populated">
                    <div className="elementor-element elementor-element-fe74466 elementor-widget elementor-widget-heading" data-id="fe74466" data-element_type="widget" data-widget_type="heading.default">
                      <div className="elementor-widget-container">
                        <h3 className="elementor-heading-title elementor-size-default">
                          {productCenterTitle}
                        </h3>
                      </div>
                    </div>
                    <div className="elementor-element elementor-element-073ea1a elementor-widget__width-initial elementor-widget elementor-widget-heading" data-id="073ea1a" data-element_type="widget" data-widget_type="heading.default" style={{ fontFamily: '"Arial", Sans-serif', fontSize: '16px', fontWeight: 500, lineHeight: '1.4em', color: 'var(--e-global-color-0505232, #888888)' }}>
                      <div className="elementor-widget-container" style={{ fontFamily: '"Arial", Sans-serif', fontSize: '16px', fontWeight: 500, lineHeight: '1.4em', color: 'var(--e-global-color-0505232, #888888)' }}>
                        <p className="elementor-heading-title elementor-size-default" style={{ fontFamily: '"Arial", Sans-serif', fontSize: '16px', fontWeight: 500, lineHeight: '1.4em', color: 'var(--e-global-color-0505232, #888888)' }}>
                          {productCenterDescription}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 内层section：两列布局 - 产品分类 + 产品网格 - 25% + 75% */}
              <section className="elementor-section elementor-inner-section elementor-element elementor-element-285f9bd elementor-section-boxed elementor-section-height-default elementor-section-height-default" data-id="285f9bd" data-element_type="section" style={{ maxWidth: '1200px', margin: '0 auto', paddingLeft: '20px', paddingRight: '20px', boxSizing: 'border-box', width: '100%' }}>
                <div className="elementor-container elementor-column-gap-default" style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', alignItems: 'flex-start', width: '100%', boxSizing: 'border-box', maxWidth: '100%', paddingLeft: '0', paddingRight: '0' }}>
                  {/* 左列：产品分类菜单 - 25%宽度 */}
                  <div className="elementor-column elementor-col-25 elementor-inner-column elementor-element elementor-element-92c5863" data-id="92c5863" data-element_type="column" data-settings='{"background_background":"classic"}' style={{ backgroundColor: '#fff', width: '25%', flex: '0 0 25%', maxWidth: '25%', minWidth: '25%', flexShrink: 0, flexGrow: 0, display: 'flex', flexDirection: 'column' }}>
                    <div className="elementor-widget-wrap elementor-element-populated">
                      <ProductCategories />
                    </div>
                  </div>

                  {/* 右列：产品网格 - 75%宽度 */}
                  <div className="elementor-column elementor-col-75 elementor-inner-column elementor-element elementor-element-ee8c04a" data-id="ee8c04a" data-element_type="column" style={{ width: '75%', flex: '0 0 75%', maxWidth: '75%', minWidth: '75%', flexShrink: 0, flexGrow: 0, display: 'flex', flexDirection: 'column' }}>
                    <div className="elementor-widget-wrap elementor-element-populated">
                      <div className="elementor-element elementor-element-30c0430 elementor-widget elementor-widget-ucaddon_post_grid" data-id="30c0430" data-element_type="widget" data-widget_type="ucaddon_post_grid.default">
                        <div className="elementor-widget-container">
                          <ProductGrid limit={6} featured={true} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </section>

            {/* 合作案例区域 - 匹配Elementor模板结构 */}
            <section className="cooperation-case-section elementor-section elementor-top-section elementor-element elementor-element-08e26db elementor-section-boxed elementor-section-height-default elementor-section-height-default" data-id="08e26db" data-element_type="section" data-settings='{"background_background":"classic"}' style={{ backgroundColor: '#FFFFFF', background: '#FFFFFF', padding: '50px 0' }}>
              <div className="elementor-container elementor-column-gap-default" style={{ maxWidth: '1200px', margin: '0 auto', paddingLeft: '20px', paddingRight: '20px', boxSizing: 'border-box', width: '100%' }}>
                <div className="elementor-column elementor-col-100 elementor-top-column elementor-element elementor-element-16aac7c" data-id="16aac7c" data-element_type="column" style={{ paddingLeft: '0', paddingRight: '0' }}>
                  <div className="elementor-widget-wrap elementor-element-populated">
                    {/* 标题 */}
                    <div className="elementor-element elementor-element-11b4481 elementor-widget elementor-widget-heading" data-id="11b4481" data-element_type="widget" data-widget_type="heading.default">
                      <div className="elementor-widget-container">
                        <h3 className="elementor-heading-title elementor-size-default">{cooperationCaseTitle}</h3>
                      </div>
                    </div>
                    {/* 描述 */}
                    <div className="elementor-element elementor-element-e2f8b7c elementor-widget elementor-widget-heading" data-id="e2f8b7c" data-element_type="widget" data-widget_type="heading.default" style={{ fontFamily: '"Arial", Sans-serif', fontSize: '16px', fontWeight: 500, lineHeight: '1.4em', color: 'var(--e-global-color-0505232, #888888)' }}>
                      <div className="elementor-widget-container" style={{ fontFamily: '"Arial", Sans-serif', fontSize: '16px', fontWeight: 500, lineHeight: '1.4em', color: 'var(--e-global-color-0505232, #888888)' }}>
                        <p className="elementor-heading-title elementor-size-default" style={{ fontFamily: '"Arial", Sans-serif', fontSize: '16px', fontWeight: 500, lineHeight: '1.4em', color: 'var(--e-global-color-0505232, #888888)' }}>{cooperationCaseDescription}</p>
                      </div>
                    </div>
                    
                    {/* 内层section：两列布局 - 图片画廊 + 案例列表 - 50% + 50% */}
                    <section className="elementor-section elementor-inner-section elementor-element elementor-element-7d12428 elementor-section-boxed elementor-section-height-default elementor-section-height-default" data-id="7d12428" data-element_type="section">
                      <div className="elementor-container elementor-column-gap-no">
                        {/* 左列：图片画廊 - 50%宽度 */}
                        <div className="elementor-column elementor-col-50 elementor-inner-column elementor-element elementor-element-9b8ec0a" data-id="9b8ec0a" data-element_type="column">
                          <div className="elementor-widget-wrap elementor-element-populated">
                            <div className="elementor-element elementor-element-9a25304 elementor-widget elementor-widget-gallery" data-id="9a25304" data-element_type="widget" data-settings='{"columns":2,"gap":{"unit":"px","size":22,"sizes":[]},"image_hover_animation":"grow","lazyload":"yes","gallery_layout":"grid","columns_tablet":2,"columns_mobile":1,"gap_tablet":{"unit":"px","size":10,"sizes":[]},"gap_mobile":{"unit":"px","size":10,"sizes":[]},"link_to":"file","aspect_ratio":"3:2","content_hover_animation":"fade-in"}' data-widget_type="gallery.default">
                              <div className="elementor-widget-container">
                                <CaseGallery />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* 右列：案例列表 - 50%宽度 */}
                        <div className="elementor-column elementor-col-50 elementor-inner-column elementor-element elementor-element-02a54fc" data-id="02a54fc" data-element_type="column" data-settings='{"background_background":"classic"}' style={{ backgroundColor: '#F8F8F8' }}>
                          <div className="elementor-widget-wrap elementor-element-populated">
                            {/* Latest Cases 标题 */}
                            <div className="elementor-element elementor-element-01a3176 elementor-widget elementor-widget-heading" data-id="01a3176" data-element_type="widget" data-widget_type="heading.default">
                              <div className="elementor-widget-container">
                                <h4 className="elementor-heading-title elementor-size-default">Latest Cases</h4>
                              </div>
                            </div>
                            {/* 案例列表 */}
                            <div className="elementor-element elementor-element-8b460f2 relate elementor-widget elementor-widget-ucaddon_post_list" data-id="8b460f2" data-element_type="widget" data-widget_type="ucaddon_post_list.default">
                              <div className="elementor-widget-container">
                                <CaseList limit={5} />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>
              </div>
            </section>

            {/* Banner 图片区域 - 全宽 */}
            <BannerImage />

            {/* News Center 区域 */}
            <NewsCenter />

            {/* 复制 Product Center Section - Footer 上方 - 包含 Why VIKDA 内容 */}
            <section className="product-center-section elementor-section elementor-top-section elementor-element elementor-element-204db98-copy elementor-section-full_width elementor-section-height-default elementor-section-height-default" data-id="204db98-copy" data-element_type="section" data-settings='{"background_background":"classic"}' style={{ backgroundColor: '#F8F8F8', background: '#F8F8F8', backgroundImage: 'none', width: '100vw', position: 'relative', left: '50%', right: '50%', marginLeft: '-50vw', marginRight: '-50vw', maxWidth: 'none', padding: '50px 0', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <WhyVIKDA />
            </section>
          </div>
        </div>
      </article>
    </Layout>
  );
}
